<template>
  <section>
    <div v-if="!presentation.isPublicSearch">
      <p class="mb-3 text-xs share-to-community__title" v-if="!title">
        {{ $i18nPresenter.t(`Any AhaSlides user can get a copy of this presentation,`
          + `with your notes, from the Template Library`) }}
        <a
          href="#"
          @click.prevent="clickLearnMore()"
          class="text-primary-100 text-link"
        >{{ ` ` }} {{ $i18nPresenter.t('Learn more...') }}
        </a>
      </p>
      <p class="mb-3 text-xs share-to-community__title" v-else>
        {{ $i18nPresenter.t(title) }}
        <a
          href="#"
          @click.prevent="clickLearnMore()"
          class="text-primary-100 text-link"
        >
          <span v-if="isTreatmentShareV2">{{ $i18nPresenter.t('Template Library') }}</span>
          <span v-else>{{ $i18nPresenter.t('Learn more...') }}</span>
        </a>
      </p>
      <aha-button
        v-if="!useInShareV2Modal"
        variant="primary"
        class="mb-1"
        @click="publish"
        :submitting="isPublishing"
        :disabled="!isOwnerOfPresentation || isDisableEditPresentation"
      >
        {{ $i18nPresenter.t('Publish') }}
      </aha-button>
      <aha-button
        v-else
        class="mb-1"
        spinnerVariant="white"
        spinnerSize="xs"
        @click="publish"
        :disabled="!isOwnerOfPresentation || isDisableEditPresentation"
      >
        <span v-if="!isPublishing">{{ $i18nPresenter.t('Publish') }}</span>
        <aha-spinner
          v-if="isPublishing"
          :variant="'white'"
        />
      </aha-button>
    </div>
    <div v-else>
      <div
        v-if="showEditInfo || isTreatmentShareV2"
        class="info-template-card mb-4 p-6"
        :class="{'info-template-card-no-border' : useInShareV2Modal}"
      >
        <p class="inline-flex items-center text-xs text-green-50 mb-4">
          <aha-icon
            class="text-green-50"
            name="circle-check"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          />
          <b class="ml-1">{{ $i18nPresenter.t('Published') }}</b>
        </p>
        <span
          class="edit inline-flex float-right text-link"
          @click="onClickEditInfo()"
          v-show="isOwnerOfPresentation && !isDisableEditPresentation"
        >
          <aha-icon
            name="pen"
            viewBox="0 0 22 22"
            width="14"
            height="14"
            class="mb-1 mr-1"
          />
          {{ $i18nPresenter.t('Edit') }}
        </span>
        <div
          class="template-card mb-4"
        >
          <div
            class="template-thumbnail"
          >
            <img
              class="object-cover w-full h-full"
              @load="onThumbnailLoad"
              v-if="presentation.thumbnailImage || presentation.customThumbnailImage"
              v-show="isThumbnailLoaded"
              :src="getThumbnailUrl"
              alt="thumbnail"
            >
            <img
              v-else
              class="object-cover w-full h-full"
              v-show="!isThumbnailLoaded"
              :src="defaultThumbnail"
              alt="Thumbnail default"
            />
            <div class="presentation-statistic absolute flex">
              <span class="ml-1 mr-3">
                <span>
                  <aha-icon
                    class="icon"
                    name="slides"
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                  />
                </span>
                {{ numOfSlides | k_formatter }}
              </span>
              <span class="mr-2">
                <span>
                  <aha-icon
                    class="icon"
                    name="download-2"
                    viewBox="0 0 16 20"
                    width="12"
                    height="12"
                  />
                </span>
                {{ timesCopied | k_formatter }}
              </span>
            </div>
          </div>
          <div class="flex w-full p-2 pb-4">
            <img
              v-if="isOfficial"
              class="mr-2"
              :src="require('@/assets/images/icon/aha-official-avt.svg')"
            >
            <aha-avatar
              v-else
              class="mt-1 ml-1 mr-2"
              :name="getAuthorName"
              :image="getAuthorAvt"
              :width="28"
              :height="28"
            />
            <div class="flex-wrap inline-flex w-full" style="max-width: 270px;">
              <b class="template-name text-sm w-full" :title="presentation.name">
                {{ presentation.name }}
              </b>
              <span class="template-author">
                <img
                  v-if="isOfficial"
                  class="inline mr-0.5"
                  :src="require('@/assets/images/icon/author-checked.svg')"
                >
                <span class="text-base-80 text-xs mt-0.5">
                  {{ isOfficial ? 'AhaSlides Official' : getAuthorName }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <b class="text-xs">{{ $i18nPresenter.t('Description') }}</b>
        <span class="template-description text-base-80 -mt-4">
          {{ getPresentationDescription }}
        </span>
      </div>
      <div
        v-else
        class="info-basic mb-4 flex"
      >
        <div class="presentation-image mr-2">
          <div v-if="presentation.customThumbnailImage || presentation.thumbnailImage">
            <img
              class="object-cover w-28 h-full rounded"
              v-if="presentation.customThumbnailImage"
              :src="presentation.customThumbnailImage"
            >
            <img
              class="object-cover w-28 h-full rounded"
              v-else
              :src="presentation.thumbnailImage"
            >
          </div>
          <img
            class="object-cover w-28 h-full rounded"
            v-else
            src="@/assets/images/icon/default-thumbnail.svg"
          >
        </div>
        <div>
          <p class="text-xs text-green-50">
            <aha-icon
              class="text-green-50"
              name="circle-check"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            />
            {{ $i18nPresenter.t('Published') }}
          </p>
          <p class="presentation-title" :title="presentation.name">
            {{ presentation.name }}
          </p>
          <p v-if="timesCopied" class="mr-4 flex flex-row items-center text-xs mt-1">
            <span class="icon-container">
              <aha-icon
                class="icon-download-2 text-secondary-50 -mt-1 mr-1"
                name="download-2"
                viewBox="0 0 14 16"
                width="9"
              />
            </span>
            {{ timesCopied | formatNumber }} download{{ timesCopied > 1 ? 's' : '' }}
          </p>
        </div>
      </div>
      <p :class="{'mt-4' : showEditInfo || isTreatmentShareV2}">
        <aha-button
          class="mr-4"
          variant="base"
          @click="clickViewTemplate(presentation)"
        >
          <span class="font-bold ml-1">{{ $i18nPresenter.t('View template') }}
            <aha-icon
              width="20"
              height="20"
              name="open-new-window"
            />
          </span>
        </aha-button>
        <aha-link
          class="mb-1 font-normal underline"
          @click="confirmUnpublish"
          v-show="isOwnerOfPresentation && !isDisableEditPresentation"
        >
          {{ $i18nPresenter.t('Unpublish') }}
        </aha-link>
      </p>
    </div>
    <modal-success-publish-template
      v-if="isConfirmPublishTemplateModalShowing"
      :forceEditMode="forceEditModeSuccessPublishTemplateModal"
      :message="confirmMessage"
      :published="latestPublisInfos"
      :hide="handleHideConfirmPublished"
      :hideHeader="hideSuccessPublishCommonHeader"
      :showCustomHeader="showSuccessPublishCustomHeader"
    />
    <modal-confirm-not-publish-template
      v-if="isConfirmNotPublishTemplateModalShowing"
      :presentation="presentation"
      @hide="handleHideConfirmNotPublished"
      :unpublish="unpublish"
      :isPublishing="isPublishing"
    />
  </section>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Sentry from '@/services/sentry'

import ModalSuccessPublishTemplate from '@/components-v2/modals/SuccessPublishPresentations.vue'
import ModalConfirmNotPublishTemplate from '@/components-v2/modals/ConfirmNotPublishTemplate.vue'

import CommunityApi from '@/api/community'
import Utils from '@/util'
import EventBus from '@/eventBus'

import eventCategories from '@/constant/eventCategories'

const defaultThumbnail = require('@/assets/images/icon/default-thumbnail.svg')

export default {
  components: {
    ModalSuccessPublishTemplate,
    ModalConfirmNotPublishTemplate,
  },
  props: {
    presentation: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    from: {
      type: String,
      required: false,
      default: '',
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    useInShareV2Modal: {
      type: Boolean,
      require: true,
      default: false,
    },
  },
  data() {
    return {
      showEditInfo: false,
      showTeamSetup: false,
      openChangeAccessCode: false,
      submitting: false,
      newAccessCode: '',
      textError: '',
      domain: '',
      presenterLink: '',
      isShowShareRecipientList: false,
      isPublishing: false,
      isConfirmPublishTemplateModalShowing: false,
      isConfirmNotPublishTemplateModalShowing: false,
      defaultThumbnail,
      isThumbnailLoaded: false,
      latestPublisInfos: {},
      hideSuccessPublishCommonHeader: false,
      showSuccessPublishCustomHeader: false,
      forceEditModeSuccessPublishTemplateModal: false,
    }
  },
  computed: {
    ...mapGetters(['isTreatmentShareV2']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    isOwnerOfPresentation() {
      if (this.currentUser.id === this.presentation.userId) {
        return true
      }
      return false
    },
    numOfSlides() {
      return this.presentation.slideCount || '0'
    },
    timesCopied() {
      return this.presentation.copyCount || '0'
    },
    isOfficial() {
      return this.presentation.User && this.presentation.User.type === 'official'
    },
    getAuthorName() {
      if (this.presentation.User) {
        return `${this.presentation.User.firstName} ${this.presentation.User.lastName}`
      }
      return ''
    },
    getAuthorAvt() {
      if (this.presentation.User) {
        return this.presentation.User.avatar
      }
      return ''
    },
    getThumbnailUrl() {
      if (this.presentation.customThumbnailImage) {
        return this.presentation.customThumbnailImage
      }
      return this.presentation.thumbnailImage
    },
    getPresentationDescription() {
      if (this.presentation.description) {
        return this.presentation.description
      }
      return this.$i18nPresenter.t('No description yet.')
    },
    confirmMessage() {
      const { name } = this.presentation
      return this.$i18nPresenter.t('yourTemplateXisNowAvailable', {
        x: name,
      })
    },
  },
  created() {
    this.latestPublisInfos = { // make sure when click edit to open setting-metadata won't lost id
      id: this.presentation.id,
    }
    this.showEditInfo = (this.$route.name === 'PresentationDetail')
  },
  methods: {
    ...mapState({
      presentations: (state) => state.presentations,
    }),
    ...mapActions(['patchPresentationAction', 'setPresentationsAction']),
    async publish() {
      this.isPublishing = true
      const category = this.from === 'presentation-list' ? 'Presentation List > Kebab menu > Share Dialog' : 'Presentation Editor > Share menu'
      if (this.useInShareV2Modal) {
        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Share menu > Publish > Publish to Template Library',
          null,
          null,
          {
            presentationId: this.presentation.id,
            userId: this.currentUser.id,
            isPresentationOwner: this.presentation.userId === this.currentUser.id,
          },
        )
      } else {
        Utils.mixpanelTracking(category, 'Publish to Template Library')
      }
      try {
        const res = await CommunityApi.publishPresentation({
          presentationIds: [this.presentation.id],
        })
        // update presentation detail
        this.patchPresentationAction({
          isPublicSearch: true,
        })
        // update presentation list
        const newPresentations = this.$store.state.presentations.map((presentation) => {
          if (presentation.id === this.presentation.id) {
            presentation.isPublicSearch = true
          }
          return presentation
        })
        this.setPresentationsAction(newPresentations)
        // because publishedInfos just array with 1 object item.
        const { presentations: latestPublisInfos } = res.body
        this.latestPublisInfos = latestPublisInfos[0] || {}
        // Change presentation here help get lastest publish present with basic needed attribute...
        this.$store.commit('updatePresentation', this.latestPublisInfos)
        this.forceEditModeSuccessPublishTemplateModal = false
        this.isConfirmPublishTemplateModalShowing = true
        this.hideSuccessPublishCommonHeader = true
        this.showSuccessPublishCustomHeader = true
        const el = document.getElementById('zsiq_float')
        if (el) {
          el.style.display = 'none'
        }
        this.$nextTick(() => {
          Utils.toggleDoubleModal('inactive')
        })
        Utils.mixpanelTracking(category, 'Publish successfully', null, null, {
          id: this.latestPublisInfos.id || null,
          name: this.latestPublisInfos.name || null,
          description: this.latestPublisInfos.description || null,
          customThumbnailImage: this.latestPublisInfos.customThumbnailImage || null,
        })
      } catch (e) {
        Utils.showToastError(this.$i18nPresenter.t('You have performed the actions too quickly. Please try again in a few minutes.'))
        Sentry.captureException({
          error: 'public template',
        })
      } finally {
        this.isPublishing = false
      }
    },
    async confirmUnpublish() {
      Utils.mixpanelTracking('Presentation List > Kebab menu > Share Dialog', 'Open Unpublish modal', '', this.presentation.id)
      this.isConfirmNotPublishTemplateModalShowing = true
      this.$nextTick(() => {
        Utils.toggleDoubleModal('inactive')
      })
    },
    async unpublish() {
      this.isPublishing = true
      try {
        await CommunityApi.unpublishPresentation({
          presentationIds: [this.presentation.id],
        })

        // update presentation detail
        this.patchPresentationAction({
          isPublicSearch: false,
        })
        // update presentation list
        const newPresentations = this.$store.state.presentations.map((presentation) => {
          if (presentation.id === this.presentation.id) {
            presentation.isPublicSearch = false
          }
          return presentation
        })
        this.setPresentationsAction(newPresentations)
        Utils.showToastSuccess(this.$i18nPresenter.t('Your presentation has been unpublished'))
        return true
      } catch (e) {
        // wait till ES finishes syncing to all nodes in cluster
        Utils.showToastError(this.$i18nPresenter.t('You have performed the actions too quickly. Please try again in a few minutes.'))
        Sentry.captureException({
          error: 'public template',
        })
        return false
      } finally {
        Utils.mixpanelTracking('Presentation List > Kebab menu > Share Dialog', 'Unpublish successfully', '', this.presentation.id)
        this.isPublishing = false
      }
    },
    handleHideConfirmPublished() {
      Utils.toggleDoubleModal('active')
      this.isConfirmPublishTemplateModalShowing = false
    },
    handleHideConfirmNotPublished() {
      Utils.toggleDoubleModal('active')
      this.isConfirmNotPublishTemplateModalShowing = false
    },
    clickLearnMore() {
      const category = this.from === 'presentation-list' ? 'Presentation List' : 'Presentation Editor'
      if (this.useInShareV2Modal) {
        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Share menu > Publish > Template library',
          null,
          null,
          {
            presentationId: this.presentation.id,
            userId: this.currentUser.id,
            isPresentationOwner: this.presentation.userId === this.currentUser.id,
          },
        )
      } else {
        Utils.mixpanelTracking(category, 'Open Share menu > Learn more')
      }
      window.open('https://help.ahaslides.com/portal/en/kb/articles/publishing-your-presentation-to-ahaslides-template-library', '_blank')
    },
    clickEditInfo(presentation) {
      EventBus.$emit('show-setting-popup', { defaultExpandedAccordion: 'information' })
      Utils.mixpanelTracking('Presentation Editor', 'Publish to Template Library > Edit Info', null, null, {
        presentationId: presentation.id,
        sourcePresentation: presentation.sourcePresentation,
      })
    },
    onThumbnailLoad() {
      this.isThumbnailLoaded = true
    },
    onClickEditInfo() {
      this.showEditInfo = true
      this.forceEditModeSuccessPublishTemplateModal = true // force show tab edit
      this.isConfirmPublishTemplateModalShowing = true // show  modal edit toogle
      this.hideSuccessPublishCommonHeader = false // default modal's header, turn on
      this.showSuccessPublishCustomHeader = false // custom header, disable
      const el = document.getElementById('zsiq_float')
      if (el) {
        el.style.display = 'none'
      }
      Utils.mixpanelTracking('Editor > Share > Publish to Template Library', 'Publish to Template Library > Edit Info')
    },
    clickViewTemplate(presentation) {
      let category = ''
      if (this.$route.name === 'PresentationDetail') {
        category = 'Editor > Share > Publish to Template Library'
      } else if (this.$route.name === 'Presentations') {
        category = 'Presentation list > Share > Publish to Template Library'
      }
      Utils.mixpanelTracking(category, 'Publish successful > Preview template')
      const preViewRoute = this.$router.resolve({
        path: '/apps/template-library',
        query: { preview: presentation.shareCode },
      })
      window.open(preViewRoute.href, 'blank')
    },
  },
}
</script>

<style lang="scss" scoped>
// styling: detail info outsite my presentations
.presentation-title {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
  overflow: hidden;
  line-height: 18px;
  @apply font-bold text-sm;
}

.presentation-image {
  flex-shrink: 0;
}

.icon-container {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  background: #EFF1F7;
  position: relative;
  margin-right: 3px;

  .icon-download-2 {
    position: absolute;
    top: 5px;
    right: 1px
  }
}
.edit {
  color: #348EF6;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
}
//styling: detail info in editor
.info-template-card {
  @apply rounded-lg;
  border: solid 1px #EBEBEB;
}
.info-template-card-no-border {
  border: none !important;
  margin-bottom: unset;
  padding: unset;
  width: 64% !important;
}
.template-card {
    @apply rounded-lg shadow-card;
    background: white;
    width: 100%;
    display: inline-grid;
    position: relative;
    .template-author {
      align-items: center;
      display: flex;
    }
  }
  .template-thumbnail {
    @apply relative mb-2 flex items-center justify-center rounded-xl;
    width: 100%;
    max-width: 330px;
    img {
      border-top-left-radius: 0.5rem;
      border-top-right-radius: 0.5rem
    }
  }
  .template-name {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
  }
  .presentation-statistic {
    border-radius: 1rem;
    bottom: 0.5rem;
    right: 0.5rem;
    background: #3F4557;
    color: white;
    opacity: 0.6;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
  .template-description {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    white-space: pre-line;
    -webkit-box-orient: vertical;
  }

.learn-more {
  margin-top: 2px;
}
</style>
